import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EscalationPanel({ lang, t, sessionId, onClose }) {
  const [channel, setChannel] = useState("callback");
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/chat/escalate`, {
        session_id: sessionId || "no-session",
        name: form.name,
        email: form.email,
        phone: form.phone,
        channel,
        note: form.note,
      });
      setSent(true);
      toast.success(t.escalateSuccess);
    } catch (err) {
      console.error("[chat] escalation failed:", err);
      toast.error(t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="flex-1 overflow-y-auto p-5 bg-white" data-testid="chat-escalate-panel">
        <div className="h-full flex flex-col items-center justify-center text-center px-4">
          <div className="w-14 h-14 rounded-full bg-[#1D3F94]/10 text-[#1D3F94] flex items-center justify-center mb-5">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h3 className="text-[18px] font-semibold text-[#1d1d1f] tracking-[-0.01em]">
            {lang === "it" ? "Richiesta inviata" : "Request sent"}
          </h3>
          <p className="mt-2 text-[13px] text-[#6e6e73] max-w-[280px] leading-relaxed">
            {t.escalateSuccess}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="mt-6 text-[13px] text-[#1D3F94] font-medium hover:underline"
            data-testid="chat-escalate-close"
          >
            {t.escalateBack}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-5 bg-white" data-testid="chat-escalate-panel">
      <button
        type="button"
        onClick={onClose}
        className="text-[12px] text-[#6e6e73] hover:text-[#1d1d1f] mb-4"
        data-testid="chat-escalate-back"
      >
        {t.escalateBack}
      </button>
      <h3 className="text-[17px] font-semibold text-[#1d1d1f] tracking-[-0.01em]">{t.escalateTitle}</h3>
      <p className="mt-1.5 text-[13px] text-[#6e6e73]">{t.escalateBody}</p>

      <div className="mt-4 grid grid-cols-2 gap-2">
        {[
          { id: "callback", label: t.channelCallback },
          { id: "email", label: t.channelEmail },
        ].map((c) => (
          <button
            type="button"
            key={c.id}
            onClick={() => setChannel(c.id)}
            data-testid={`chat-escalate-channel-${c.id}`}
            className={`px-3 py-2.5 rounded-xl text-[13px] font-medium border transition-all ${
              channel === c.id
                ? "bg-[#1D3F94] text-white border-[#1D3F94]"
                : "bg-white text-[#1d1d1f] border-[#e4e4e7] hover:border-[#1D3F94]/40"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="mt-4 space-y-3">
        <input
          required
          placeholder={t.escalateNameLabel + " *"}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="chat-escalate-name"
          className="w-full bg-[#f5f5f7] rounded-xl px-3.5 py-2.5 text-[14px] outline-none focus:ring-2 focus:ring-[#1D3F94]/30"
        />
        <input
          required
          type="email"
          placeholder={t.escalateEmailLabel + " *"}
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="chat-escalate-email"
          className="w-full bg-[#f5f5f7] rounded-xl px-3.5 py-2.5 text-[14px] outline-none focus:ring-2 focus:ring-[#1D3F94]/30"
        />
        <input
          required
          type="tel"
          placeholder={t.escalatePhoneLabel + " *"}
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          data-testid="chat-escalate-phone"
          className="w-full bg-[#f5f5f7] rounded-xl px-3.5 py-2.5 text-[14px] outline-none focus:ring-2 focus:ring-[#1D3F94]/30"
        />
        <textarea
          rows={2}
          placeholder={t.escalateNoteLabel}
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          data-testid="chat-escalate-note"
          className="w-full bg-[#f5f5f7] rounded-xl px-3.5 py-2.5 text-[14px] outline-none focus:ring-2 focus:ring-[#1D3F94]/30 resize-none"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="chat-escalate-submit"
          className="w-full bg-[#1D3F94] text-white py-2.5 rounded-full text-[14px] font-medium disabled:opacity-60 hover:bg-[#152f6f] transition-colors"
        >
          {loading ? "…" : t.escalateSubmit}
        </button>
      </form>
    </div>
  );
}
